import {
  LTO_FORM_KEYS,
  OFFER_TARGET_TYPE,
  OFFER_TYPE,
  TIMER_TYPE,
} from "../constants";
import {
  OFFER_DIRECTION,
  TRIGGER_TYPE,
  buildUnifiedMetadata,
  getDefaultShowAtCheckout,
} from "./unifiedOffer";
import {
  buildBackendTriggerConfig,
  mapDirection,
  mapTriggerType,
} from "./offerContract";

const INTL_ROW_KEYS = ["tiers", "rows", "schedules"];

/**
 * Backend rules for `value_international` (scoot-backend
 * `OfferTriggerService._validate_international_values`): allowed only for
 * `value_unit: "flat"`, and must be present on EVERY price row or none.
 * The builders carry `value_international` straight from each form row, so
 * here we simply strip it when the offer is percent-based or only partially
 * filled. Pure — returns the same object shape.
 */
export const sanitizeInternationalValues = (config, value_unit) => {
  if (!config) return config;
  const priceRows = [
    ...(config.tiers || []),
    ...(config.rows || []),
    ...(config.schedules || []),
    ...(config.default ? [config.default] : []),
  ];
  const withIntl = priceRows.filter((r) => r.value_international !== undefined);
  const keep = value_unit === "flat" && withIntl.length === priceRows.length;
  if (keep || !withIntl.length) return config;
  const strip = (rows) =>
    (rows || []).map((row) => {
      const next = { ...row };
      delete next.value_international;
      return next;
    });
  const next = { ...config };
  INTL_ROW_KEYS.forEach((key) => {
    if (next[key]) next[key] = strip(next[key]);
  });
  if (next.default) next.default = strip([next.default])[0];
  return next;
};

/**
 * Domestic `trigger_config` for the current trigger, with each row's
 * `value_international` kept only when the backend will accept it (flat, on
 * every row). Null for schedule/evergreen (no trigger_config).
 */
export const buildTriggerConfigWithInternational = (formState = {}) => {
  const trigger =
    formState[LTO_FORM_KEYS.TRIGGER_TYPE] || TRIGGER_TYPE.SCHEDULE;
  const valueUnit = formState[LTO_FORM_KEYS.VALUE_UNIT];
  const config = buildBackendTriggerConfig({
    trigger,
    tiers: formState[LTO_FORM_KEYS.TIERS],
    threshold_unit: formState[LTO_FORM_KEYS.THRESHOLD_UNIT],
    value_unit: valueUnit,
    vip_apply_by: formState[LTO_FORM_KEYS.VIP_APPLY_BY],
    vip_windows: formState[LTO_FORM_KEYS.VIP_WINDOWS],
    vip_start_date: formState[LTO_FORM_KEYS.VIP_START_DATE],
    vip_end_date: formState[LTO_FORM_KEYS.VIP_END_DATE],
    batch_values: formState[LTO_FORM_KEYS.BATCH_VALUES],
  });
  return sanitizeInternationalValues(config, valueUnit);
};

/**
 * Single mapping from form values → the unified `metadata` object we send on
 * both create and update. Keeping it in one place guarantees the two payloads
 * can never drift.
 */
export const buildUnifiedMetadataFromForm = (formState = {}) =>
  buildUnifiedMetadata({
    direction: formState[LTO_FORM_KEYS.DIRECTION],
    trigger_type:
      formState[LTO_FORM_KEYS.TRIGGER_TYPE] || TRIGGER_TYPE.SCHEDULE,
    tiers: formState[LTO_FORM_KEYS.TIERS],
    show_at_checkout: formState[LTO_FORM_KEYS.SHOW_AT_CHECKOUT],
    sync_end_to_start: formState[LTO_FORM_KEYS.SYNC_END_TO_START],
    threshold_unit: formState[LTO_FORM_KEYS.THRESHOLD_UNIT],
    value_unit: formState[LTO_FORM_KEYS.VALUE_UNIT],
    evergreen_start_event: formState[LTO_FORM_KEYS.EVERGREEN_START_EVENT],
    vip_apply_by: formState[LTO_FORM_KEYS.VIP_APPLY_BY],
    vip_windows: formState[LTO_FORM_KEYS.VIP_WINDOWS],
    vip_start_date: formState[LTO_FORM_KEYS.VIP_START_DATE],
    vip_end_date: formState[LTO_FORM_KEYS.VIP_END_DATE],
    batch_values: formState[LTO_FORM_KEYS.BATCH_VALUES],
  });

/**
 * The exact body POSTed to `host/offer/create`.
 * Pure — no network, no sessionStorage.
 */
export const buildCreateOfferPayload = (formState = {}) => {
  const isPercentage =
    formState[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE;
  const direction =
    formState[LTO_FORM_KEYS.DIRECTION] || OFFER_DIRECTION.DISCOUNT;
  const trigger =
    formState[LTO_FORM_KEYS.TRIGGER_TYPE] || TRIGGER_TYPE.SCHEDULE;
  // Evergreen is a property of the trigger, not the legacy timer_type field
  // (which can go stale when the trigger is switched to a threshold shape).
  const isEvergreen = trigger === TRIGGER_TYPE.EVERGREEN;
  const timerType = isEvergreen ? TIMER_TYPE.EVERGREEN : TIMER_TYPE.FIXED;

  let payload = {
    title: formState.offerName,
    target_type: formState.target_type,
    offer_type: formState[LTO_FORM_KEYS.OFFER_TYPE],
    timer_type: timerType,
    // Backend contract (top-level ints); metadata keeps the UI-friendly copy.
    direction: mapDirection(direction),
    trigger_type: mapTriggerType(trigger),
    show_at_checkout:
      formState[LTO_FORM_KEYS.SHOW_AT_CHECKOUT] ??
      getDefaultShowAtCheckout(direction),
    metadata: buildUnifiedMetadataFromForm(formState),
  };

  // Domestic trigger_config, with per-row `value_international` merged in
  // when an Amount offer has the international checkbox on.
  const triggerConfig = buildTriggerConfigWithInternational(formState);
  if (triggerConfig) payload.trigger_config = triggerConfig;
  // Backend only allows sync with a Schedule trigger.
  if (
    formState[LTO_FORM_KEYS.SYNC_END_TO_START] &&
    trigger === TRIGGER_TYPE.SCHEDULE
  ) {
    payload.sync_end_to_offering_start = true;
  }

  if (isEvergreen) {
    payload.evergreen_duration_value =
      formState[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];
    payload.evergreen_duration_unit =
      formState[LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT];
    // Backend stores + returns this (detail/applied_offer); validated
    // against page_view|waitlist|signup.
    if (formState[LTO_FORM_KEYS.EVERGREEN_START_EVENT]) {
      payload.evergreen_start_event =
        formState[LTO_FORM_KEYS.EVERGREEN_START_EVENT];
    }
  } else {
    payload.start_date = formState.start_date;
    payload.start_time = formState.start_time;
    payload.end_date = formState.end_date;
    payload.end_time = formState.end_time;
  }

  // Single-price keys are sent only when a real value exists — tiered /
  // batch-wise offers carry per-row values instead, and the backend
  // rejects explicit nulls. Never emit `price_inr: null`.
  if (isPercentage) {
    const pct = parseFloat(formState[LTO_FORM_KEYS.PERCENTAGE_VALUE]);
    if (Number.isFinite(pct)) payload.discount_percentage = pct;
  } else {
    const inr = parseFloat(formState[LTO_FORM_KEYS.INR_PRICE]);
    if (Number.isFinite(inr)) payload.price_inr = inr.toFixed(2);
  }

  if (formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING) {
    payload.listing_uids = formState[LTO_FORM_KEYS.LISTINGS] || [];
  } else if (
    formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE
  ) {
    payload.dynamic_link_uuids =
      formState[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [];
  }

  if (!isPercentage && formState[LTO_FORM_KEYS.USD_CHECKBOX]) {
    const usd = parseFloat(formState[LTO_FORM_KEYS.USD_PRICE]);
    if (Number.isFinite(usd)) {
      payload = {
        ...payload,
        price_international: usd.toFixed(2),
      };
    }
    if (!isEvergreen) {
      payload.start_date_international = formState.start_date_international;
      payload.start_time_international = formState.start_time_international;
      payload.end_date_international = formState.end_date_international;
      payload.end_time_international = formState.end_time_international;
    }
  }

  return payload;
};

/**
 * Editable half of the `host/offer/update` body. Only values travel —
 * offering/trigger structure is locked server-side, so structural keys are
 * never sent (the backend 400s on any drift).
 */
export const buildUpdateOfferMetadata = (formState = {}) => {
  const direction =
    formState[LTO_FORM_KEYS.DIRECTION] || OFFER_DIRECTION.DISCOUNT;
  const updated = {
    title: formState.offerName,
    show_at_checkout:
      formState[LTO_FORM_KEYS.SHOW_AT_CHECKOUT] ??
      getDefaultShowAtCheckout(direction),
  };
  const triggerConfig = buildTriggerConfigWithInternational(formState);
  if (triggerConfig) updated.trigger_config = triggerConfig;
  return updated;
};
